import { Dialog } from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BillStatus, LANGUAGES, OrderItemStatus, SHIPPING_FEE } from '../../../../../consts';
import { useSystemContext } from '../../../../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../../../../context/UserMenuContext';
import useCreateOrder from '../../../../../hook/useCreateOrder';
import { FoodItem, IMultiOrder, Order } from '../../../../../interfaces/types';
import { formatPriceItem } from '../../../../../utils';
import { getPaymentNoticeAlerts } from '../../../../../utils/cancellationAlert';
import PAYMENT from '../../../../DeliveryInformation/components/Payment';
import PaymentNoticeDialog from '../../../../../components/PaymentNoticeDialog/PaymentNoticeDialog';

const STATUS_STYLES = {
  PREPARING: { color: 'text-[#B42318]', bgColor: 'bg-[#FEF3F2]' },
  READY_FOR_PICK_UP: { color: 'text-[#B54708]', bgColor: 'bg-[#FFFAEB]' },
  DELIVERING: { color: 'text-[#026AA2]', bgColor: 'bg-[#F0F9FF]' },
  DELIVERED: { color: 'text-[#027A48]', bgColor: 'bg-[#ECFDF3]' },
  CANCELLED: { color: 'text-[#B42318]', bgColor: 'bg-[#FEF3F2]' },
};

export const STATUS_COLOR_MAP = {
  [OrderItemStatus.PREPARING]: { ...STATUS_STYLES.PREPARING, label: 'PREPARING' },
  [OrderItemStatus.DAMAGED_PREPARING]: { ...STATUS_STYLES.DELIVERING, label: 'DELIVERING' },
  [OrderItemStatus.READY_FOR_PICK_UP]: { ...STATUS_STYLES.READY_FOR_PICK_UP, label: 'READY_FOR_PICK_UP' },
  [OrderItemStatus.DAMAGED_READY_FOR_PICK_UP]: { ...STATUS_STYLES.DELIVERING, label: 'DELIVERING' },
  [OrderItemStatus.DELIVERING]: { ...STATUS_STYLES.DELIVERING, label: 'DELIVERING' },
  [OrderItemStatus.DAMAGED_DELIVERING]: { ...STATUS_STYLES.DELIVERING, label: 'DELIVERING' },
  [OrderItemStatus.DELIVERED]: { ...STATUS_STYLES.DELIVERED, label: 'DELIVERED' },
  [OrderItemStatus.CANCELLED]: { ...STATUS_STYLES.DELIVERED, label: 'REFUNDED' },
};

const OrderItem: React.FC<{ order: Order; onShowDetail: (order: Order) => void; onReorder: (order: Order) => void }> = ({ order, onReorder, onShowDetail }) => {
  const { t } = useTranslation();
  const [openPayment, setOpenPayment] = useState(false);
  const [openPaymentNotice, setOpenPaymentNotice] = useState(false);
  const [isSubmittingPaymentNotice, setIsSubmittingPaymentNotice] = useState(false);
  const { checkOrderV2 } = useCreateOrder();
  const [openError, setOpenError] = useState(false);
  const [errorFood, setErrorFood] = useState<string>('');
  const { stadiumInfo } = useSystemContext();
  const { listRestaurantData, listRestaurantWithFood } = useUserMenuContext();
  const isPreOrderPayment = order?.is_pre_order === true || String(order?.order_type || '').includes('pre_order');
  const paymentNoticeAlerts = useMemo(
    () =>
      getPaymentNoticeAlerts({
        order,
        restaurants: [...listRestaurantWithFood, ...listRestaurantData],
        language: stadiumInfo?.language,
      }),
    [listRestaurantData, listRestaurantWithFood, order, stadiumInfo?.language]
  );

  const handlePlaceOrder = async () => {
    const isErrorOrder = await checkOrderV2(order as any);
    if (isErrorOrder) {
      setOpenPayment(true);
    } else {
      const errorFoodItem = sessionStorage.getItem('ErrorFood');
      if (errorFoodItem) {
        setErrorFood(errorFoodItem);
      }
      setOpenError(true);
    }
  };

  const handleClosePayment = () => {
    setOpenPayment(false);
  };

  const handleOpenPaymentNotice = async () => {
    if (isSubmittingPaymentNotice) return;
    if (!isPreOrderPayment || paymentNoticeAlerts.length === 0) {
      setIsSubmittingPaymentNotice(true);
      try {
        await handlePlaceOrder();
      } finally {
        setIsSubmittingPaymentNotice(false);
      }
      return;
    }

    setOpenPaymentNotice(true);
  };

  const handleClosePaymentNotice = () => {
    if (isSubmittingPaymentNotice) return;
    setOpenPaymentNotice(false);
  };

  const handleSubmitPaymentNotice = async () => {
    if (isSubmittingPaymentNotice) return;

    setIsSubmittingPaymentNotice(true);
    try {
      setOpenPaymentNotice(false);
      await handlePlaceOrder();
    } finally {
      setIsSubmittingPaymentNotice(false);
    }
  };

  const handleCloseErrorModal = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    sessionStorage.removeItem('ErrorFood');
    setOpenError(false);
  };

  const formatJapaneseDate = (date: Date): string => {
    const year = format(date, 'yyyy', { locale: ja });
    const month = format(date, 'MM', { locale: ja });
    const day = format(date, 'dd', { locale: ja });
    const time = format(date, 'HH:mm', { locale: ja });
    return `${year}年${month}月${day}日, ${time}`;
  };

  const multiOrder = useMemo(() => {
    if (!order?.foods || order.foods.length === 0) return false;
    const restaurantIds = new Set<string>();
    order.foods.forEach((food: FoodItem) => {
      if (food.restaurantId) {
        restaurantIds.add(food.restaurantId);
      }
    });
    return restaurantIds.size > 1;
  }, [order]);

  const [multiTotal, multiPreparing] = useMemo(() => {
    const multi = (order?.multi_orders ?? []) as IMultiOrder[];
    const total = multi.length;
    const preparingCount = multi.filter((m) => m.status === OrderItemStatus.DELIVERED).length;
    return [total, preparingCount];
  }, [order?.multi_orders]);

  const [price, formattedTime, totalItem] = useMemo<[number, string, number]>(() => {
    if (!order) return [0, '', 0];

    const totalItem =
      (order.foods?.reduce(
        (total: number, food: FoodItem) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
        0
      ) || 0) +
      (order.promotion_foods?.reduce(
        (total: number, food: FoodItem) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
        0
      ) || 0);

    const shippingFee = order.order_status === OrderItemStatus.CANCELLED ? 0 : totalItem === 0 ? 0 : SHIPPING_FEE;

    const calculateSubtotal = (foods: FoodItem[]) => {
      return (
        foods?.reduce((total: number, food: FoodItem) => {
          const actualQuantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
          return total + actualQuantity * food.item_price;
        }, 0) || 0
      );
    };

    const totalPrice = calculateSubtotal(order.foods) + calculateSubtotal(order.promotion_foods);
    const totalPayment = totalPrice + shippingFee;
    const formattedPrice = totalPayment;

    const date = new Date(order.order_create_time?.seconds ? order.order_create_time.seconds * 1000 : Date.now());
    const formattedTime = stadiumInfo?.language === LANGUAGES.JAPAN ? formatJapaneseDate(date) : format(date, 'dd/MM/yyyy, HH:mm');

    return [formattedPrice, formattedTime || '', totalItem];
  }, [order, stadiumInfo?.language]);

  const totalValidItems = useMemo(() => {
    const totalItemQuantity = order.foods.reduce((total, item) => total + item.item_quantity, 0);
    const totalDeletedQuantity = order.foods.reduce((total, item) => total + (item.item_deleted_quantity ?? 0), 0);

    const isPartiallyCancelled = totalItemQuantity > totalDeletedQuantity && totalDeletedQuantity > 0 && order.order_status === OrderItemStatus.DELIVERED;

    if (isPartiallyCancelled) {
      return { ...STATUS_STYLES.DELIVERED, label: 'PARTIALLY_CANCELED_REFUNDED' };
    }

    if (order.order_status === OrderItemStatus.PREPARING && order.bill_status === 'NOT_CLEARED') {
      return { ...STATUS_STYLES.PREPARING, label: 'UNCOMPLETE_PAYMENT' };
    }

    return STATUS_COLOR_MAP[order.order_status];
  }, [order.foods, order.order_status, order.bill_status]);

  return (
    <div
      aria-hidden
      onClick={(e) => {
        onShowDetail(order);
      }}
      className="p-4 flex flex-col gap-3 bg-white border-b border-[#EAECF0]"
    >
      <div className="flex items-center justify-between">
        <p className="text-[#344054] w-fit text-sm capitalize font-medium leading-5 rounded-2xl bg-[#F2F4F7] px-2 py-[2px]">{order.id}</p>

        <p className={`w-fit text-xs capitalize font-medium leading-[18px] rounded-2xl px-2 py-[2px] ${totalValidItems.bgColor} ${totalValidItems.color}`}>
          {t(`${totalValidItems.label}`)}
        </p>
      </div>
      <div className="flex items-center gap-3 w-full">
        <img
          src={multiOrder ? stadiumInfo?.stadium_profile_pic : order.foods[0]?.restaurant_profile_pic}
          alt={multiOrder ? stadiumInfo?.name : order.foods[0]?.restaurant_name}
          className="max-w-[100px] h-[100px] shrink-0 rounded-lg w-auto"
        />
        <div className="flex flex-col gap-1 w-full">
          {multiOrder ? (
            <div className="flex items-center gap-1 justify-between">
              <p className="text-[#1D2939] text-base leading-6 font-medium">{stadiumInfo?.name}</p>
              <div className="flex items-center gap-1 px-2 rounded-[9px] bg-[#EAECF0] text-[12px] leading-[18px] w-fit">
                <span className="text-[#98A2B3] font-normal">{t('NUMBER_OF_STORES')}:</span>
                <span className="text-[#344054] font-semibold">{multiTotal}</span>
              </div>
            </div>
          ) : (
            <p className="text-[#1D2939] text-base leading-6 font-medium">{order.foods[0]?.restaurant_name}</p>
          )}
          <p className="text-black-500 text-xs leading-[18px] font-light">{formattedTime}</p>
          <p className="text-[#475467] text-xs leading-[18px] font-normal line-clamp-2">
            {order.foods.map((item) => item.item_name).join('; ')}
            {order.promotion_foods && order.promotion_foods.length > 0 && (
              <>
                {'; '}
                {order.promotion_foods.map((item) => item.item_name).join('; ')}
              </>
            )}
          </p>
        </div>
      </div>
      <div className="border-solid border-[2px] border-[#EAECF0]" />
      <div className="flex items-center gap-3">
        <div className="flex-1 flex items-center gap-2">
          <p className="text-[#1D2939] text-[18px] leading-7 font-medium">¥ {formatPriceItem(price)}</p>
          <div className=" border-l border-[#EAECF0] h-4" />
          <p className="text-[#667085] text-sm leading-5 font-normal">
            {totalItem} {t('MENU_CARD_ITEMS')}
          </p>
        </div>
        {order.bill_status === BillStatus.CLEARED ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReorder(order);
            }}
            className="border border-[#D0D5DD] px-[14px] py-2 rounded-lg text-sm font-semibold"
          >
            {t('RE_ORDER')}
          </button>
        ) : order.bill_status === BillStatus.PAYMENT_PENDING ? (
          <span className="text-[#667085] text-sm leading-5 font-medium text-right">
            {t('PAYMENT_PROCESSING', { defaultValue: 'Payment is being processed' })}
          </span>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPaymentNotice();
            }}
            disabled={isSubmittingPaymentNotice}
            className="border border-[#D0D5DD] px-[14px] py-2 rounded-lg text-sm font-semibold bg-primary-green text-white"
          >
            {t('COMPLETE_PAYMENT')}
          </button>
        )}
        <PaymentNoticeDialog
          open={openPaymentNotice}
          onCancel={handleClosePaymentNotice}
          onSubmit={handleSubmitPaymentNotice}
          isSubmitting={isSubmittingPaymentNotice}
          alerts={paymentNoticeAlerts}
        />
        <PAYMENT open={openPayment} onClose={handleClosePayment} totalPrice={price} orderId={order.id} />
      </div>
      <Dialog
        open={openError}
        onClose={(e: React.MouseEvent<HTMLDivElement>) => {
          handleCloseErrorModal(e);
        }}
        sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}
      >
        <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()} className="p-6 flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <p className="text-black-900 text-lg font-semibold leading-7">{errorFood ? `${errorFood} ${t('HAS_BEEN_DISABLE')}` : t('LISTRESTAURANT_MODAL_TITLE')}</p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default OrderItem;
